import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Leaf, Users, Package, ArrowRight, Mail } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { API } from "@/lib/site";

const ICONS = [BookOpen, Leaf, Users, Package];

const Shop = () => {
    const { t, lang } = useLanguage();
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);

    const notify = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSending(true);
        try {
            await axios.post(`${API}/shop-notify`, { email, language: lang });
            toast.success(lang === "de" ? "Du erhältst eine Nachricht zum Launch." : "Te avisaremos en el lanzamiento.");
            setEmail("");
        } catch {
            toast.error(lang === "de" ? "Fehler beim Speichern." : "Error al guardar.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="shop" data-testid="shop-section" className="py-24 md:py-32 bg-ella-ink text-ella-cream relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
                <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-ella-gold blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-ella-champagne blur-3xl" />
            </div>

            <div className="ella-container relative">
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-3">
                        <span className="gold-hairline" />
                        <span className="ella-overline-light">{t.shop.overline}</span>
                        <span className="gold-hairline" />
                    </div>
                    <h2 className="ella-display text-4xl md:text-5xl lg:text-6xl mt-5 text-ella-cream" data-testid="shop-title">
                        {t.shop.title}
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-ella-cream/70 font-light max-w-2xl leading-relaxed">{t.shop.subtitle}</p>
                </div>

                <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {t.shop.items.map((item, i) => {
                        const Icon = ICONS[i % ICONS.length];
                        return (
                            <div
                                key={i}
                                data-testid={`shop-item-${i}`}
                                className="group relative p-8 md:p-9 rounded-2xl border border-ella-line-dark bg-ella-charcoal hover:border-ella-gold/60 transition-all duration-500 flex flex-col min-h-[320px]"
                            >
                                {/* Top row: icon + badge */}
                                <div className="flex items-start justify-between gap-3">
                                    <span className="w-11 h-11 rounded-xl bg-ella-onyx flex items-center justify-center text-ella-gold flex-shrink-0">
                                        <Icon className="w-[18px] h-[18px]" strokeWidth={1.4} />
                                    </span>
                                    <span className="px-2.5 py-1 rounded-full bg-ella-gold/15 text-[9px] uppercase tracking-[0.22em] text-ella-gold whitespace-nowrap">
                                        {t.shop.soon_badge}
                                    </span>
                                </div>

                                <h3 className="font-display text-[22px] md:text-2xl mt-7 text-ella-cream leading-tight">
                                    {item.title}
                                </h3>
                                <p className="mt-3 text-[13px] md:text-sm text-ella-cream/65 leading-[1.65] font-light">
                                    {item.desc}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* Notify form */}
                <form onSubmit={notify} data-testid="shop-notify-form" className="mt-16 max-w-lg mx-auto">
                    <div className="flex items-center gap-2 p-2 rounded-full border border-ella-line-dark bg-ella-charcoal backdrop-blur">
                        <Mail className="w-4 h-4 ml-4 text-ella-gold flex-shrink-0" strokeWidth={1.5} />
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t.shop.notify}
                            className="flex-1 bg-transparent outline-none text-ella-cream placeholder:text-ella-cream/40 text-sm py-2 min-w-0"
                            data-testid="shop-notify-input"
                        />
                        <button type="submit" disabled={sending} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-ella-gold text-white text-xs tracking-wide hover:bg-ella-gold-dark transition-colors disabled:opacity-60 flex-shrink-0" data-testid="shop-notify-submit">
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
};

export default Shop;
